/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { CrystalProperties } from '../models/CrystalProperties.ts';

// Curated experimental properties for the built-in presets. Values are
// rounded room-temperature numbers from CRC Handbook / NIST / Springer
// Materials. Where a property does not physically apply (no boiling point
// for diamond — it sublimes; no band gap for metals — they're conductors)
// or where a reliable single number isn't available, the field is omitted
// and the gallery surfaces a blank.
//
// `formula` is the only required field; the rest are optional so partial
// coverage is fine. Derived fields (molarMass, atomsPerCell, cellVolume,
// packingFraction) are computed at runtime from the CrystalState and
// merged into this map — don't pre-populate them here.
//
// Keys MUST match the `name` field of the corresponding preset in
// presets.ts. User-renamed, CIF-imported, or AI-generated crystals are
// looked up by their stored name; AI-generated crystals carry their own
// `predicted` properties stamped on at generation time (see CrystalState).

export const CRYSTAL_PROPERTIES: Record<string, CrystalProperties> = {
  // ─── Rock-salt halides / oxides ────────────────────────────────────────
  'Sodium Chloride': {
    formula: 'NaCl',
    density: 2.17,
    meltingPoint: 801,
    boilingPoint: 1413,
    specificHeat: 0.864,
    thermalConductivity: 6.5,
    thermalExpansion: 39.6,
    debyeTemperature: 321,
    formationEnthalpy: -411.2,
    youngsModulus: 40,
    bulkModulus: 24.5,
    shearModulus: 14.7,
    poissonRatio: 0.25,
    mohsHardness: 2.5,
    vickersHardness: 0.18,
    bandGap: 8.5,
    electricalResistivity: 1e13,
    dielectricConstant: 5.9,
    refractiveIndex: 1.544,
    magneticSusceptibility: -30.3,
  },
  'Potassium Chloride': {
    formula: 'KCl',
    density: 1.984,
    meltingPoint: 770,
    boilingPoint: 1420,
    specificHeat: 0.69,
    thermalConductivity: 6.7,
    thermalExpansion: 36.9,
    debyeTemperature: 235,
    formationEnthalpy: -436.5,
    bulkModulus: 17.4,
    shearModulus: 9.4,
    poissonRatio: 0.28,
    mohsHardness: 2.0,
    vickersHardness: 0.085,
    bandGap: 8.4,
    electricalResistivity: 1e13,
    dielectricConstant: 4.86,
    refractiveIndex: 1.49,
    magneticSusceptibility: -39.0,
  },
  'Sodium Fluoride': {
    formula: 'NaF',
    density: 2.558,
    meltingPoint: 993,
    boilingPoint: 1704,
    specificHeat: 1.1,
    thermalConductivity: 9.2,
    thermalExpansion: 36,
    debyeTemperature: 492,
    formationEnthalpy: -576.6,
    youngsModulus: 80,
    bulkModulus: 46.5,
    shearModulus: 31,
    poissonRatio: 0.24,
    mohsHardness: 3.2,
    vickersHardness: 0.6,
    bandGap: 11.5,
    electricalResistivity: 1e9,
    dielectricConstant: 5.1,
    refractiveIndex: 1.326,
    magneticSusceptibility: -16.4,
  },
  'Lithium Fluoride': {
    formula: 'LiF',
    density: 2.635,
    meltingPoint: 845,
    boilingPoint: 1676,
    specificHeat: 1.55,
    thermalConductivity: 11.3,
    thermalExpansion: 37,
    debyeTemperature: 732,
    formationEnthalpy: -616.0,
    bulkModulus: 65.1,
    shearModulus: 55,
    poissonRatio: 0.21,
    mohsHardness: 3.3,
    vickersHardness: 1.2,
    bandGap: 13.6,
    electricalResistivity: 1e11,
    dielectricConstant: 9.0,
    refractiveIndex: 1.392,
    magneticSusceptibility: -10.1,
  },
  'Potassium Bromide': {
    formula: 'KBr',
    density: 2.75,
    meltingPoint: 734,
    boilingPoint: 1435,
    specificHeat: 0.435,
    thermalConductivity: 4.8,
    thermalExpansion: 38,
    debyeTemperature: 174,
    formationEnthalpy: -393.8,
    youngsModulus: 27,
    bulkModulus: 14.8,
    shearModulus: 7.6,
    poissonRatio: 0.28,
    mohsHardness: 2.0,
    vickersHardness: 0.063,
    bandGap: 7.4,
    electricalResistivity: 1e12,
    dielectricConstant: 4.9,
    refractiveIndex: 1.559,
    magneticSusceptibility: -49.2,
  },
  'Cesium Iodide': {
    formula: 'CsI',
    density: 4.51,
    meltingPoint: 632,
    boilingPoint: 1280,
    specificHeat: 0.2,
    thermalConductivity: 1.1,
    thermalExpansion: 48.6,
    debyeTemperature: 124,
    formationEnthalpy: -346.6,
    youngsModulus: 5.3,
    bulkModulus: 12.7,
    shearModulus: 6.4,
    poissonRatio: 0.27,
    mohsHardness: 2.0,
    vickersHardness: 0.4,
    bandGap: 6.4,
    electricalResistivity: 1e10,
    dielectricConstant: 5.65,
    refractiveIndex: 1.788,
    magneticSusceptibility: -82.6,
  },
  'Cesium Chloride': {
    formula: 'CsCl',
    density: 3.99,
    meltingPoint: 645,
    boilingPoint: 1295,
    specificHeat: 0.288,
    thermalConductivity: 0.89,
    thermalExpansion: 50,
    debyeTemperature: 175,
    formationEnthalpy: -443.0,
    youngsModulus: 25,
    bulkModulus: 17.3,
    shearModulus: 7.6,
    poissonRatio: 0.27,
    mohsHardness: 2.5,
    vickersHardness: 0.5,
    bandGap: 8.3,
    electricalResistivity: 1e11,
    dielectricConstant: 7.2,
    refractiveIndex: 1.642,
    magneticSusceptibility: -53,
  },
  'Calcium Fluoride': {
    formula: 'CaF₂',
    density: 3.18,
    meltingPoint: 1418,
    boilingPoint: 2533,
    specificHeat: 0.854,
    thermalConductivity: 9.7,
    thermalExpansion: 18.9,
    debyeTemperature: 510,
    formationEnthalpy: -1228.0,
    youngsModulus: 76,
    bulkModulus: 82.0,
    shearModulus: 42,
    poissonRatio: 0.28,
    mohsHardness: 4.0,
    vickersHardness: 1.78,
    bandGap: 12.1,
    electricalResistivity: 1e10,
    dielectricConstant: 6.81,
    refractiveIndex: 1.434,
    magneticSusceptibility: -28.0,
  },
  'Strontium Fluoride': {
    formula: 'SrF₂',
    density: 4.24,
    meltingPoint: 1477,
    boilingPoint: 2460,
    specificHeat: 0.59,
    thermalConductivity: 1.42,
    thermalExpansion: 18,
    debyeTemperature: 380,
    formationEnthalpy: -1216.3,
    youngsModulus: 90,
    bulkModulus: 70.0,
    shearModulus: 34,
    poissonRatio: 0.29,
    mohsHardness: 3.5,
    vickersHardness: 1.4,
    bandGap: 10.7,
    electricalResistivity: 1e12,
    dielectricConstant: 6.5,
    refractiveIndex: 1.438,
    magneticSusceptibility: -38,
  },
  'Magnesium Oxide': {
    formula: 'MgO',
    density: 3.58,
    meltingPoint: 2852,
    boilingPoint: 3600,
    specificHeat: 0.928,
    thermalConductivity: 45,
    thermalExpansion: 10.5,
    debyeTemperature: 940,
    formationEnthalpy: -601.6,
    youngsModulus: 310,
    bulkModulus: 162,
    shearModulus: 130,
    poissonRatio: 0.18,
    mohsHardness: 5.5,
    vickersHardness: 9.2,
    bandGap: 7.8,
    electricalResistivity: 1e12,
    dielectricConstant: 9.65,
    refractiveIndex: 1.736,
    magneticSusceptibility: -10.2,
  },
  'Calcium Oxide': {
    formula: 'CaO',
    density: 3.34,
    meltingPoint: 2613,
    boilingPoint: 2850,
    specificHeat: 0.752,
    thermalConductivity: 16,
    thermalExpansion: 11.8,
    debyeTemperature: 605,
    formationEnthalpy: -634.9,
    youngsModulus: 220,
    bulkModulus: 113,
    shearModulus: 81,
    poissonRatio: 0.21,
    mohsHardness: 4.5,
    vickersHardness: 6,
    bandGap: 7.0,
    electricalResistivity: 1e10,
    dielectricConstant: 11.8,
    refractiveIndex: 1.838,
    magneticSusceptibility: -15,
  },
  'Lithium Oxide': {
    formula: 'Li₂O',
    density: 2.013,
    meltingPoint: 1438,
    boilingPoint: 2600,
    specificHeat: 1.99,
    thermalConductivity: 14,
    thermalExpansion: 17,
    debyeTemperature: 580,
    formationEnthalpy: -595.8,
    youngsModulus: 144,
    bulkModulus: 81,
    shearModulus: 60,
    poissonRatio: 0.24,
    mohsHardness: 6,
    bandGap: 7.99,
    electricalResistivity: 1e10,
    dielectricConstant: 7.4,
    refractiveIndex: 1.644,
    magneticSusceptibility: -10,
  },
  'Lead Sulfide': {
    formula: 'PbS',
    density: 7.6,
    meltingPoint: 1118,
    boilingPoint: 1281,
    specificHeat: 0.208,
    thermalConductivity: 2.5,
    thermalExpansion: 20.3,
    debyeTemperature: 227,
    formationEnthalpy: -100.4,
    youngsModulus: 70,
    bulkModulus: 53,
    shearModulus: 27,
    poissonRatio: 0.28,
    mohsHardness: 2.5,
    vickersHardness: 0.7,
    bandGap: 0.37,
    electricalResistivity: 0.1,
    dielectricConstant: 169,
    refractiveIndex: 3.91,
    magneticSusceptibility: -84,
  },
  'Sodium Iodide': {
    formula: 'NaI',
    density: 3.667,
    meltingPoint: 661,
    boilingPoint: 1304,
    specificHeat: 0.288,
    thermalConductivity: 1.8,
    formationEnthalpy: -287.8,
    bulkModulus: 15.1,
    shearModulus: 5.7,
    poissonRatio: 0.27,
    mohsHardness: 1.5,
    vickersHardness: 0.05,
    bandGap: 5.9,
    electricalResistivity: 1e7,
    dielectricConstant: 7.28,
    refractiveIndex: 1.774,
    magneticSusceptibility: -56,
  },
  'Strontium Oxide': {
    formula: 'SrO',
    density: 4.7,
    meltingPoint: 2531,
    boilingPoint: 3200,
    specificHeat: 0.45,
    thermalConductivity: 10,
    formationEnthalpy: -592.0,
    bulkModulus: 91,
    shearModulus: 56,
    poissonRatio: 0.2,
    mohsHardness: 3.5,
    bandGap: 5.9,
    electricalResistivity: 1e10,
    dielectricConstant: 13.3,
    refractiveIndex: 1.81,
    magneticSusceptibility: -35,
  },
  'Barium Fluoride': {
    formula: 'BaF₂',
    density: 4.893,
    meltingPoint: 1368,
    boilingPoint: 2260,
    specificHeat: 0.41,
    thermalConductivity: 11.7,
    formationEnthalpy: -1207.1,
    bulkModulus: 56.8,
    shearModulus: 25,
    poissonRatio: 0.31,
    mohsHardness: 3.0,
    vickersHardness: 0.82,
    bandGap: 9.1,
    electricalResistivity: 1e11,
    dielectricConstant: 7.32,
    refractiveIndex: 1.474,
    magneticSusceptibility: -36,
  },
  'Magnesium Fluoride': {
    formula: 'MgF₂',
    density: 3.148,
    meltingPoint: 1263,
    boilingPoint: 2260,
    specificHeat: 1.001,
    thermalConductivity: 11.6,
    formationEnthalpy: -1124.2,
    youngsModulus: 138,
    bulkModulus: 102,
    shearModulus: 55,
    poissonRatio: 0.27,
    mohsHardness: 5.5,
    vickersHardness: 4.15,
    bandGap: 12.4,
    electricalResistivity: 1e15,
    // Rutile-structure MgF₂ is birefringent (ε_⊥ ≈ 5.4, ε_∥ ≈ 4.87); the
    // 5.2 number is the isotropic mean used for parallel-coords plotting.
    dielectricConstant: 5.2,
    refractiveIndex: 1.378,
    magneticSusceptibility: -17.5,
  },
  'Tin Dioxide': {
    formula: 'SnO₂',
    density: 6.95,
    meltingPoint: 1630,
    boilingPoint: 1800,
    specificHeat: 0.353,
    thermalConductivity: 40,
    thermalExpansion: 4.0,
    debyeTemperature: 700,
    formationEnthalpy: -577.6,
    youngsModulus: 263,
    bulkModulus: 218,
    shearModulus: 100,
    poissonRatio: 0.29,
    mohsHardness: 6.5,
    vickersHardness: 12,
    bandGap: 3.6,
    // Undoped n-type semiconductor (oxygen-vacancy donors) at RT.
    // F-doped SnO₂ for transparent conductors is several orders smaller.
    electricalResistivity: 4e-4,
    dielectricConstant: 9.6,
    refractiveIndex: 2.006,
    magneticSusceptibility: -41,
  },
  'Calcium Titanate': {
    formula: 'CaTiO₃',
    density: 3.98,
    meltingPoint: 1975,
    specificHeat: 0.71,
    thermalConductivity: 4.5,
    thermalExpansion: 11.4,
    formationEnthalpy: -1660.6,
    youngsModulus: 270,
    bulkModulus: 173,
    shearModulus: 106,
    poissonRatio: 0.27,
    mohsHardness: 5.5,
    vickersHardness: 8,
    bandGap: 3.5,
    electricalResistivity: 1e10,
    dielectricConstant: 170,
    refractiveIndex: 2.34,
    magneticSusceptibility: -10,
  },
  'Manganese Oxide': {
    formula: 'MnO',
    density: 5.37,
    meltingPoint: 1945,
    specificHeat: 0.71,
    thermalConductivity: 8,
    thermalExpansion: 11,
    debyeTemperature: 478,
    formationEnthalpy: -385.2,
    youngsModulus: 250,
    bulkModulus: 158,
    shearModulus: 67,
    poissonRatio: 0.18,
    mohsHardness: 5.5,
    vickersHardness: 4.5,
    bandGap: 3.6,
    electricalResistivity: 1e6,
    dielectricConstant: 18,
    refractiveIndex: 2.16,
    // Strongly paramagnetic above T_N (Curie-Weiss tail of the
    // Mn²⁺ S=5/2 moments); χ ≈ +4850 ×10⁻⁶ cm³/mol at 300 K.
    magneticSusceptibility: 4850,
    // Type-II antiferromagnet; Néel temperature ≈ 116 K.
    curieTemperature: 116,
  },
  'Nickel Oxide': {
    formula: 'NiO',
    density: 6.67,
    meltingPoint: 1955,
    specificHeat: 0.6,
    thermalConductivity: 35,
    thermalExpansion: 12.8,
    debyeTemperature: 600,
    formationEnthalpy: -239.7,
    youngsModulus: 245,
    bulkModulus: 199,
    shearModulus: 90,
    poissonRatio: 0.2,
    mohsHardness: 5.5,
    vickersHardness: 5,
    bandGap: 3.8,
    electricalResistivity: 1e13,
    dielectricConstant: 11.9,
    refractiveIndex: 2.33,
    // Paramagnetic at RT below T_N (Curie-Weiss tail of Ni²⁺ S=1
    // moments); χ ≈ +660 ×10⁻⁶ cm³/mol.
    magneticSusceptibility: 660,
    // Mott-Hubbard antiferromagnetic insulator; Néel temperature ≈ 525 K.
    curieTemperature: 525,
  },
  'Lead Titanate': {
    formula: 'PbTiO₃',
    density: 7.83,
    meltingPoint: 1290,
    specificHeat: 0.34,
    thermalConductivity: 4.2,
    thermalExpansion: 11,
    debyeTemperature: 420,
    formationEnthalpy: -1208,
    youngsModulus: 105,
    bulkModulus: 70,
    shearModulus: 42,
    poissonRatio: 0.3,
    mohsHardness: 4.5,
    vickersHardness: 5,
    bandGap: 3.6,
    electricalResistivity: 1e9,
    dielectricConstant: 200,
    refractiveIndex: 2.66,
    magneticSusceptibility: -10,
    curieTemperature: 763,
  },
  'Zinc Oxide': {
    formula: 'ZnO',
    density: 5.606,
    meltingPoint: 1975,
    specificHeat: 0.495,
    thermalConductivity: 54,
    thermalExpansion: 4.31,
    debyeTemperature: 416,
    formationEnthalpy: -350.5,
    youngsModulus: 140,
    bulkModulus: 142.4,
    shearModulus: 44,
    poissonRatio: 0.36,
    mohsHardness: 4.5,
    vickersHardness: 4.0,
    bandGap: 3.37,
    // Undoped ZnO is intrinsically n-type from O-vacancy / Zn-interstitial
    // donors at RT; ρ varies several orders with stoichiometry.
    electricalResistivity: 10,
    dielectricConstant: 8.66,
    refractiveIndex: 2.0,
    magneticSusceptibility: -27.2,
  },

  // ─── Covalent / diamond-structure ──────────────────────────────────────
  Diamond: {
    formula: 'C',
    density: 3.515,
    sublimationPoint: 3825,
    specificHeat: 0.509,
    thermalConductivity: 2200,
    thermalExpansion: 1.0,
    debyeTemperature: 2230,
    formationEnthalpy: 1.9,
    youngsModulus: 1220,
    bulkModulus: 442,
    shearModulus: 535,
    poissonRatio: 0.2,
    mohsHardness: 10,
    vickersHardness: 100,
    bandGap: 5.47,
    electricalResistivity: 1e16,
    workFunction: 4.81,
    dielectricConstant: 5.7,
    refractiveIndex: 2.418,
    magneticSusceptibility: -5.9,
  },
  Silicon: {
    formula: 'Si',
    density: 2.329,
    meltingPoint: 1414,
    boilingPoint: 3265,
    specificHeat: 0.712,
    thermalConductivity: 149,
    thermalExpansion: 2.6,
    debyeTemperature: 645,
    formationEnthalpy: 0,
    youngsModulus: 165,
    bulkModulus: 97.6,
    shearModulus: 66.5,
    poissonRatio: 0.22,
    mohsHardness: 7,
    vickersHardness: 11.3,
    bandGap: 1.12,
    electricalResistivity: 6.4e2,
    workFunction: 4.85,
    dielectricConstant: 11.68,
    refractiveIndex: 3.42,
    magneticSusceptibility: -3.9,
  },
  Germanium: {
    formula: 'Ge',
    density: 5.323,
    meltingPoint: 938,
    boilingPoint: 2833,
    specificHeat: 0.32,
    thermalConductivity: 60.2,
    thermalExpansion: 5.9,
    debyeTemperature: 374,
    youngsModulus: 103,
    bulkModulus: 75,
    shearModulus: 41,
    poissonRatio: 0.26,
    mohsHardness: 6.0,
    vickersHardness: 8.8,
    bandGap: 0.66,
    electricalResistivity: 0.46,
    workFunction: 5.0,
    dielectricConstant: 16,
    refractiveIndex: 4.0,
    magneticSusceptibility: -11.6,
  },
  'Gray Tin': {
    formula: 'Sn',
    density: 5.769,
    // α-Sn (gray tin) is stable below 13.2 °C; transforms to β-Sn above. The
    // value commonly cited is this α↔β transition temperature, not a true
    // melting point, but it's what materials tables list.
    meltingPoint: 13.2,
    specificHeat: 0.227,
    thermalConductivity: 66,
    thermalExpansion: 4.7,
    debyeTemperature: 200,
    formationEnthalpy: 2.5,
    youngsModulus: 53,
    bulkModulus: 53,
    shearModulus: 23,
    poissonRatio: 0.3,
    mohsHardness: 1.5,
    vickersHardness: 0.5,
    bandGap: 0.08,
    electricalResistivity: 2e-6,
    refractiveIndex: 4.0,
    magneticSusceptibility: -37,
  },
  Lonsdaleite: {
    formula: 'C',
    density: 3.51,
    sublimationPoint: 3550,
    specificHeat: 0.51,
    thermalConductivity: 2100,
    thermalExpansion: 1.0,
    debyeTemperature: 2200,
    youngsModulus: 1200,
    bulkModulus: 444,
    shearModulus: 530,
    poissonRatio: 0.2,
    mohsHardness: 10,
    vickersHardness: 110,
    bandGap: 4.5,
    electricalResistivity: 1e16,
    // Same tetrahedrally-bonded C density as cubic diamond, so the
    // dielectric response is essentially identical (5.7).
    dielectricConstant: 5.7,
    refractiveIndex: 2.4,
    magneticSusceptibility: -6,
  },
  'Silicon Carbide': {
    formula: 'SiC',
    density: 3.21,
    sublimationPoint: 2730,
    specificHeat: 0.69,
    thermalConductivity: 360,
    thermalExpansion: 4.0,
    debyeTemperature: 1200,
    formationEnthalpy: -73.2,
    youngsModulus: 410,
    bulkModulus: 220,
    shearModulus: 192,
    poissonRatio: 0.17,
    mohsHardness: 9.5,
    vickersHardness: 27,
    bandGap: 3.26,
    electricalResistivity: 1e3,
    dielectricConstant: 9.7,
    refractiveIndex: 2.65,
    magneticSusceptibility: -12.8,
  },
  'Cubic Boron Nitride': {
    formula: 'BN',
    density: 3.48,
    meltingPoint: 2973,
    sublimationPoint: 2973,
    specificHeat: 0.8,
    thermalConductivity: 740,
    thermalExpansion: 1.2,
    debyeTemperature: 1700,
    formationEnthalpy: -254.4,
    youngsModulus: 909,
    bulkModulus: 400,
    shearModulus: 405,
    poissonRatio: 0.12,
    mohsHardness: 9.9,
    vickersHardness: 50,
    bandGap: 6.4,
    electricalResistivity: 1e10,
    dielectricConstant: 7.1,
    refractiveIndex: 2.117,
    magneticSusceptibility: -10,
  },
  'Hexagonal Boron Nitride': {
    formula: 'BN',
    density: 2.1,
    meltingPoint: 2973,
    sublimationPoint: 2973,
    specificHeat: 0.8,
    thermalConductivity: 30,
    thermalExpansion: 0.7,
    debyeTemperature: 1700,
    formationEnthalpy: -254.4,
    youngsModulus: 36,
    bulkModulus: 36,
    // Strongly anisotropic — in-plane shear is hundreds of GPa, the
    // interlayer (basal) shear is just a few. The 7 GPa polycrystalline
    // average is what tabulated values report.
    shearModulus: 7,
    poissonRatio: 0.21,
    mohsHardness: 2.0,
    vickersHardness: 0.2,
    bandGap: 5.96,
    electricalResistivity: 1e13,
    dielectricConstant: 4,
    refractiveIndex: 1.8,
    magneticSusceptibility: -27,
  },
  'Aluminum Nitride': {
    formula: 'AlN',
    density: 3.26,
    meltingPoint: 2200,
    sublimationPoint: 2200,
    specificHeat: 0.74,
    thermalConductivity: 285,
    thermalExpansion: 4.5,
    debyeTemperature: 950,
    formationEnthalpy: -318.0,
    youngsModulus: 308,
    bulkModulus: 200,
    shearModulus: 130,
    poissonRatio: 0.24,
    mohsHardness: 7.0,
    vickersHardness: 12,
    bandGap: 6.015,
    electricalResistivity: 1e11,
    dielectricConstant: 9.0,
    refractiveIndex: 2.15,
    magneticSusceptibility: -19,
  },
  'Boron Phosphide': {
    formula: 'BP',
    density: 2.97,
    meltingPoint: 1100,
    specificHeat: 0.79,
    thermalConductivity: 350,
    thermalExpansion: 3.65,
    debyeTemperature: 985,
    formationEnthalpy: -116,
    youngsModulus: 290,
    bulkModulus: 165,
    shearModulus: 165,
    poissonRatio: 0.21,
    mohsHardness: 9.5,
    vickersHardness: 32,
    bandGap: 2.0,
    electricalResistivity: 1e-3,
    dielectricConstant: 11.0,
    refractiveIndex: 3.0,
    magneticSusceptibility: -12,
  },
  'Boron Arsenide': {
    formula: 'BAs',
    density: 5.22,
    meltingPoint: 2300,
    specificHeat: 0.42,
    thermalConductivity: 1300,
    thermalExpansion: 3.85,
    debyeTemperature: 700,
    youngsModulus: 379,
    bulkModulus: 138,
    shearModulus: 142,
    poissonRatio: 0.19,
    mohsHardness: 9,
    vickersHardness: 22,
    bandGap: 1.82,
    electricalResistivity: 1e-3,
    dielectricConstant: 9.6,
    refractiveIndex: 3.29,
    magneticSusceptibility: -15,
  },
  Cristobalite: {
    formula: 'SiO₂',
    density: 2.32,
    meltingPoint: 1713,
    boilingPoint: 2230,
    specificHeat: 0.74,
    thermalConductivity: 1.4,
    thermalExpansion: 12,
    debyeTemperature: 470,
    formationEnthalpy: -908.3,
    youngsModulus: 65,
    bulkModulus: 37,
    shearModulus: 31,
    poissonRatio: 0.17,
    mohsHardness: 6.5,
    vickersHardness: 6,
    bandGap: 8.9,
    electricalResistivity: 1e10,
    dielectricConstant: 3.8,
    refractiveIndex: 1.486,
    magneticSusceptibility: -29.6,
  },

  // All-silica sodalite (SOD-type clathrasil). Properties differ from
  // dense SiO₂ polymorphs (quartz/cristobalite) because the porous
  // framework lowers density and stiffness; from natural Na/Cl sodalite
  // because there are no cage guests. Density computed from a = 8.965 Å
  // and 12 SiO₂/cell. Thermal/elastic numbers from Bibby & Dale (1985),
  // Petrovic et al. (1993) calorimetry, and Astala-Auerbach DFT (2004).
  Sodalite: {
    formula: 'SiO₂',
    density: 1.66,
    meltingPoint: 1700,
    specificHeat: 0.74,
    thermalConductivity: 0.7,
    thermalExpansion: 2,
    debyeTemperature: 460,
    formationEnthalpy: -902.5,
    youngsModulus: 55,
    bulkModulus: 35,
    shearModulus: 23,
    poissonRatio: 0.2,
    mohsHardness: 5.5,
    vickersHardness: 4.5,
    bandGap: 8.5,
    electricalResistivity: 1e12,
    dielectricConstant: 2.5,
    refractiveIndex: 1.42,
    magneticSusceptibility: -30,
  },

  // All-silica Zeolite A (LTA framework). The textbook molecular sieve;
  // commercial 3A/4A/5A grades are aluminosilicate forms with K⁺/Na⁺/Ca²⁺
  // tuning the window size. Lowest framework density of the common
  // zeolites (~12.9 T/1000 Å³), so the lowest elastic moduli too.
  // Values from ITQ-29 (the all-silica LTA, Corma et al. Nature 2004).
  'Zeolite A': {
    formula: 'SiO₂',
    density: 1.4,
    meltingPoint: 1700,
    specificHeat: 0.78,
    thermalConductivity: 0.5,
    thermalExpansion: -2,
    debyeTemperature: 420,
    formationEnthalpy: -899.5,
    youngsModulus: 35,
    bulkModulus: 20,
    shearModulus: 15,
    poissonRatio: 0.22,
    mohsHardness: 4.5,
    vickersHardness: 3.5,
    bandGap: 8.3,
    electricalResistivity: 1e12,
    dielectricConstant: 2.2,
    refractiveIndex: 1.38,
    magneticSusceptibility: -30,
  },

  // All-silica faujasite (FAU framework) = siliceous zeolite Y endmember.
  // Commercial FAU (zeolite X, zeolite Y, USY) is the workhorse of
  // catalytic cracking. Large 12-ring windows (7.4 Å) make it the most
  // open of the major zeolites. Properties from Hriljac et al. (1993)
  // and the IZA database.
  Faujasite: {
    formula: 'SiO₂',
    density: 1.27,
    meltingPoint: 1700,
    specificHeat: 0.84,
    thermalConductivity: 0.4,
    thermalExpansion: -3,
    debyeTemperature: 410,
    formationEnthalpy: -898.4,
    youngsModulus: 30,
    bulkModulus: 18,
    shearModulus: 12,
    poissonRatio: 0.24,
    mohsHardness: 4,
    vickersHardness: 3,
    bandGap: 8.2,
    electricalResistivity: 1e12,
    dielectricConstant: 2.1,
    refractiveIndex: 1.36,
    magneticSusceptibility: -30,
  },

  // All-silica MFI = silicalite-1, the parent of ZSM-5. The most important
  // catalytic zeolite (FCC, methanol-to-gasoline, xylene isomerization).
  // 10-ring channels (5.5 Å) — medium-pore sieve. Negative thermal
  // expansion ~−5 ppm/K over a wide range. Properties from Bhange &
  // Ramaswamy (2006), Astala et al. (2004) DFT.
  Silicalite: {
    formula: 'SiO₂',
    density: 1.78,
    meltingPoint: 1700,
    specificHeat: 0.76,
    thermalConductivity: 0.6,
    thermalExpansion: -5,
    debyeTemperature: 450,
    formationEnthalpy: -905.0,
    youngsModulus: 50,
    bulkModulus: 30,
    shearModulus: 21,
    poissonRatio: 0.2,
    mohsHardness: 5,
    vickersHardness: 4,
    bandGap: 8.5,
    electricalResistivity: 1e12,
    dielectricConstant: 2.4,
    refractiveIndex: 1.41,
    magneticSusceptibility: -30,
  },

  // All-silica chabazite (CHA framework) — small-pore (8-ring, 3.8 Å)
  // workhorse of automotive SCR catalysis (Cu-SSZ-13, Cu-CHA). The
  // siliceous endmember is SSZ-13. Properties from Diaz-Cabanas et al.
  // (1998), Bull et al. (2000).
  Chabazite: {
    formula: 'SiO₂',
    density: 1.51,
    meltingPoint: 1700,
    specificHeat: 0.76,
    thermalConductivity: 0.6,
    thermalExpansion: -3,
    debyeTemperature: 440,
    formationEnthalpy: -902.7,
    youngsModulus: 45,
    bulkModulus: 27,
    shearModulus: 18,
    poissonRatio: 0.21,
    mohsHardness: 5,
    vickersHardness: 4,
    bandGap: 8.4,
    electricalResistivity: 1e12,
    dielectricConstant: 2.3,
    refractiveIndex: 1.4,
    magneticSusceptibility: -30,
  },

  // All-silica mordenite (MOR framework). One-dimensional 12-ring main
  // channel (7.0 × 6.5 Å) — important in n-paraffin hydroisomerization
  // and acid catalysis. Densest of this zeolite set (17.0 T/1000 Å³).
  // Properties from Meier (1961), Alberti (1997).
  Mordenite: {
    formula: 'SiO₂',
    density: 1.92,
    meltingPoint: 1700,
    specificHeat: 0.75,
    thermalConductivity: 0.7,
    thermalExpansion: -2,
    debyeTemperature: 455,
    formationEnthalpy: -906.0,
    youngsModulus: 58,
    bulkModulus: 34,
    shearModulus: 24,
    poissonRatio: 0.2,
    mohsHardness: 5.5,
    vickersHardness: 4.5,
    bandGap: 8.5,
    electricalResistivity: 1e12,
    dielectricConstant: 2.4,
    refractiveIndex: 1.42,
    magneticSusceptibility: -30,
  },

  // ─── III-V / II-VI compound semiconductors ─────────────────────────────
  'Gallium Arsenide': {
    formula: 'GaAs',
    density: 5.317,
    meltingPoint: 1238,
    specificHeat: 0.327,
    thermalConductivity: 55,
    thermalExpansion: 5.73,
    debyeTemperature: 360,
    youngsModulus: 85.5,
    bulkModulus: 75.5,
    shearModulus: 32.9,
    poissonRatio: 0.31,
    mohsHardness: 4.5,
    vickersHardness: 6.8,
    bandGap: 1.424,
    // Semi-insulating (SI) undoped GaAs at RT; Cr- or EL2-compensated
    // wafers used for IC substrates are in this range.
    electricalResistivity: 3e8,
    dielectricConstant: 12.9,
    refractiveIndex: 3.3,
    magneticSusceptibility: -16.2,
  },
  'Gallium Phosphide': {
    formula: 'GaP',
    density: 4.138,
    meltingPoint: 1480,
    specificHeat: 0.43,
    thermalConductivity: 110,
    thermalExpansion: 4.65,
    debyeTemperature: 445,
    formationEnthalpy: -88,
    youngsModulus: 103,
    bulkModulus: 88,
    shearModulus: 56,
    poissonRatio: 0.31,
    mohsHardness: 5,
    vickersHardness: 9.5,
    bandGap: 2.26,
    electricalResistivity: 1e10,
    dielectricConstant: 11.1,
    refractiveIndex: 3.02,
    magneticSusceptibility: -28.0,
  },
  'Gallium Nitride': {
    formula: 'GaN',
    density: 6.15,
    meltingPoint: 2500,
    specificHeat: 0.49,
    thermalConductivity: 230,
    thermalExpansion: 5.6,
    debyeTemperature: 600,
    formationEnthalpy: -156.8,
    youngsModulus: 295,
    bulkModulus: 210,
    shearModulus: 121,
    poissonRatio: 0.23,
    mohsHardness: 9.0,
    vickersHardness: 15,
    bandGap: 3.39,
    electricalResistivity: 1e8,
    dielectricConstant: 9.0,
    refractiveIndex: 2.42,
    magneticSusceptibility: -16,
  },
  'Gallium Antimonide': {
    formula: 'GaSb',
    density: 5.614,
    meltingPoint: 712,
    specificHeat: 0.34,
    thermalConductivity: 32,
    thermalExpansion: 7.75,
    debyeTemperature: 266,
    formationEnthalpy: -41.8,
    youngsModulus: 76,
    bulkModulus: 57,
    shearModulus: 34,
    poissonRatio: 0.31,
    mohsHardness: 4.5,
    vickersHardness: 4.5,
    bandGap: 0.726,
    electricalResistivity: 10,
    dielectricConstant: 15.7,
    refractiveIndex: 3.82,
    magneticSusceptibility: -33,
  },
  'Indium Phosphide': {
    formula: 'InP',
    density: 4.81,
    meltingPoint: 1062,
    specificHeat: 0.31,
    thermalConductivity: 68,
    thermalExpansion: 4.6,
    debyeTemperature: 425,
    formationEnthalpy: -88.7,
    youngsModulus: 61,
    bulkModulus: 71,
    shearModulus: 34,
    poissonRatio: 0.36,
    mohsHardness: 4,
    vickersHardness: 5.0,
    bandGap: 1.344,
    electricalResistivity: 1e6,
    dielectricConstant: 12.5,
    refractiveIndex: 3.1,
    magneticSusceptibility: -22,
  },
  'Indium Arsenide': {
    formula: 'InAs',
    density: 5.667,
    meltingPoint: 942,
    specificHeat: 0.25,
    thermalConductivity: 27,
    thermalExpansion: 4.52,
    debyeTemperature: 280,
    formationEnthalpy: -58.6,
    youngsModulus: 51,
    bulkModulus: 58,
    shearModulus: 30,
    poissonRatio: 0.35,
    mohsHardness: 3.8,
    vickersHardness: 3.3,
    bandGap: 0.354,
    electricalResistivity: 1e-2,
    dielectricConstant: 15.15,
    refractiveIndex: 3.51,
    magneticSusceptibility: -42,
  },
  'Indium Antimonide': {
    formula: 'InSb',
    density: 5.775,
    meltingPoint: 527,
    specificHeat: 0.2,
    thermalConductivity: 18,
    thermalExpansion: 5.37,
    debyeTemperature: 200,
    formationEnthalpy: -30.5,
    youngsModulus: 41,
    bulkModulus: 47,
    shearModulus: 25,
    poissonRatio: 0.35,
    mohsHardness: 3.8,
    vickersHardness: 2.2,
    bandGap: 0.17,
    electricalResistivity: 5e-5,
    dielectricConstant: 16.8,
    refractiveIndex: 3.96,
    magneticSusceptibility: -65,
  },
  'Aluminum Phosphide': {
    formula: 'AlP',
    density: 2.42,
    meltingPoint: 2530,
    specificHeat: 0.73,
    thermalConductivity: 90,
    thermalExpansion: 4.5,
    debyeTemperature: 588,
    formationEnthalpy: -166.5,
    youngsModulus: 145,
    bulkModulus: 86,
    shearModulus: 47,
    poissonRatio: 0.31,
    mohsHardness: 5.5,
    vickersHardness: 9.4,
    bandGap: 2.45,
    electricalResistivity: 1e10,
    dielectricConstant: 9.8,
    refractiveIndex: 2.75,
    magneticSusceptibility: -14,
  },
  'Aluminum Arsenide': {
    formula: 'AlAs',
    density: 3.76,
    meltingPoint: 1740,
    specificHeat: 0.45,
    thermalConductivity: 91,
    thermalExpansion: 5.2,
    debyeTemperature: 446,
    formationEnthalpy: -116.3,
    youngsModulus: 100,
    bulkModulus: 78,
    shearModulus: 45,
    poissonRatio: 0.33,
    mohsHardness: 5.0,
    vickersHardness: 5.0,
    bandGap: 2.16,
    electricalResistivity: 1e8,
    dielectricConstant: 10.06,
    refractiveIndex: 3.18,
    magneticSusceptibility: -20,
  },
  'Zinc Sulfide': {
    formula: 'ZnS',
    density: 4.09,
    meltingPoint: 1850,
    specificHeat: 0.472,
    thermalConductivity: 27,
    thermalExpansion: 6.5,
    debyeTemperature: 351,
    formationEnthalpy: -206,
    youngsModulus: 75,
    bulkModulus: 77.1,
    shearModulus: 30,
    poissonRatio: 0.32,
    mohsHardness: 3.7,
    vickersHardness: 1.8,
    bandGap: 3.54,
    electricalResistivity: 1e12,
    dielectricConstant: 8.32,
    refractiveIndex: 2.37,
    magneticSusceptibility: -19.4,
  },
  'Wurtzite Zinc Sulfide': {
    formula: 'ZnS',
    density: 4.087,
    meltingPoint: 1700,
    specificHeat: 0.46,
    thermalConductivity: 27,
    thermalExpansion: 6.7,
    debyeTemperature: 351,
    formationEnthalpy: -192.6,
    youngsModulus: 75,
    bulkModulus: 76,
    shearModulus: 32,
    poissonRatio: 0.32,
    mohsHardness: 3.7,
    vickersHardness: 1.8,
    bandGap: 3.91,
    electricalResistivity: 1e12,
    dielectricConstant: 8.7,
    refractiveIndex: 2.36,
    magneticSusceptibility: -19.4,
  },
  'Zinc Selenide': {
    formula: 'ZnSe',
    density: 5.27,
    meltingPoint: 1525,
    specificHeat: 0.34,
    thermalConductivity: 18,
    thermalExpansion: 7.1,
    debyeTemperature: 270,
    formationEnthalpy: -163,
    youngsModulus: 67,
    bulkModulus: 62,
    shearModulus: 29,
    poissonRatio: 0.28,
    mohsHardness: 3.5,
    vickersHardness: 1.4,
    bandGap: 2.7,
    electricalResistivity: 1e6,
    dielectricConstant: 9.2,
    refractiveIndex: 2.4,
    magneticSusceptibility: -38.5,
  },
  'Cadmium Sulfide': {
    formula: 'CdS',
    density: 4.82,
    meltingPoint: 1750,
    specificHeat: 0.33,
    thermalConductivity: 20,
    thermalExpansion: 4.3,
    debyeTemperature: 219,
    formationEnthalpy: -161.9,
    youngsModulus: 71,
    bulkModulus: 62,
    shearModulus: 28,
    poissonRatio: 0.4,
    mohsHardness: 3.5,
    vickersHardness: 1.5,
    bandGap: 2.42,
    electricalResistivity: 1e6,
    dielectricConstant: 8.9,
    refractiveIndex: 2.529,
    magneticSusceptibility: -41.5,
  },
  'Aluminum Antimonide': {
    formula: 'AlSb',
    density: 4.218,
    meltingPoint: 1065,
    specificHeat: 0.32,
    thermalConductivity: 56,
    thermalExpansion: 4.2,
    debyeTemperature: 292,
    formationEnthalpy: -56.5,
    youngsModulus: 80,
    bulkModulus: 58.2,
    shearModulus: 31,
    poissonRatio: 0.33,
    mohsHardness: 4.0,
    vickersHardness: 4.0,
    bandGap: 1.6,
    electricalResistivity: 1e3,
    dielectricConstant: 12.0,
    refractiveIndex: 3.182,
    magneticSusceptibility: -32,
  },
  'Zinc Telluride': {
    formula: 'ZnTe',
    density: 6.34,
    meltingPoint: 1295,
    specificHeat: 0.26,
    thermalConductivity: 18,
    thermalExpansion: 8.3,
    debyeTemperature: 223,
    formationEnthalpy: -119.2,
    youngsModulus: 56,
    bulkModulus: 51,
    shearModulus: 23,
    poissonRatio: 0.36,
    mohsHardness: 3.0,
    vickersHardness: 1.0,
    bandGap: 2.26,
    electricalResistivity: 10,
    dielectricConstant: 9.7,
    refractiveIndex: 3.06,
    magneticSusceptibility: -52,
  },
  'Beryllium Oxide': {
    formula: 'BeO',
    density: 3.01,
    meltingPoint: 2507,
    boilingPoint: 4123,
    specificHeat: 1.026,
    thermalConductivity: 330,
    thermalExpansion: 9,
    debyeTemperature: 1280,
    formationEnthalpy: -609.4,
    youngsModulus: 380,
    bulkModulus: 220,
    shearModulus: 162,
    poissonRatio: 0.26,
    mohsHardness: 9.0,
    vickersHardness: 12,
    bandGap: 10.6,
    electricalResistivity: 1e15,
    dielectricConstant: 6.6,
    refractiveIndex: 1.719,
    magneticSusceptibility: -11.9,
  },
  'Cadmium Telluride': {
    formula: 'CdTe',
    density: 5.85,
    meltingPoint: 1041,
    specificHeat: 0.21,
    thermalConductivity: 6.2,
    thermalExpansion: 5.9,
    debyeTemperature: 158,
    formationEnthalpy: -100.4,
    youngsModulus: 36,
    bulkModulus: 42.4,
    shearModulus: 19,
    poissonRatio: 0.41,
    mohsHardness: 2.75,
    vickersHardness: 0.6,
    bandGap: 1.49,
    electricalResistivity: 1e3,
    dielectricConstant: 10.2,
    refractiveIndex: 2.72,
    magneticSusceptibility: -57,
  },
  // Cinnabar — trigonal α-HgS (P3₁21), the bright-red mineral form of
  // mercury sulfide. Wider band gap than the cubic β polymorph because
  // the Hg-S spiral chains have a shorter, more covalent Hg-S bond
  // (~2.36 Å) — that's what makes the visible color red, not the metallic
  // dark of Metacinnabar. Densely packed, so density is higher than β.
  Cinnabar: {
    formula: 'HgS',
    density: 8.176,
    meltingPoint: 580,
    specificHeat: 0.205,
    thermalConductivity: 2.7,
    thermalExpansion: 5.5,
    debyeTemperature: 220,
    formationEnthalpy: -58.2,
    youngsModulus: 32,
    bulkModulus: 25,
    shearModulus: 13,
    poissonRatio: 0.32,
    mohsHardness: 2.25,
    vickersHardness: 0.5,
    bandGap: 2.1,
    electricalResistivity: 1e6,
    dielectricConstant: 12,
    refractiveIndex: 3.08,
    magneticSusceptibility: -55,
  },
  // Metacinnabar — cubic β-HgS, zincblende (F-43m). Hg-S chalcogenide;
  // narrow-gap semiconductor (~0.5 eV) sitting at the bottom of the
  // II-VI series with Hg.
  Metacinnabar: {
    formula: 'HgS',
    density: 7.7,
    meltingPoint: 580,
    specificHeat: 0.21,
    thermalConductivity: 2.7,
    thermalExpansion: 4.5,
    debyeTemperature: 180,
    formationEnthalpy: -53.6,
    youngsModulus: 59,
    bulkModulus: 48,
    shearModulus: 26,
    poissonRatio: 0.3,
    mohsHardness: 3.0,
    vickersHardness: 0.7,
    bandGap: 0.5,
    electricalResistivity: 1e-2,
    dielectricConstant: 18.2,
    refractiveIndex: 2.85,
    magneticSusceptibility: -55,
  },
  // Coloradoite — HgTe zincblende (F-43m). Semimetal at room T
  // (E_g ≈ 0 eV; technically negative gap from band inversion), the
  // end-member of the Hg(1-x)Cd(x)Te MCT alloy family used for tunable-
  // wavelength infrared detectors.
  Coloradoite: {
    formula: 'HgTe',
    density: 8.17,
    meltingPoint: 670,
    specificHeat: 0.164,
    thermalConductivity: 2.5,
    thermalExpansion: 4.5,
    debyeTemperature: 145,
    formationEnthalpy: -42.0,
    youngsModulus: 46,
    bulkModulus: 42,
    shearModulus: 22,
    poissonRatio: 0.36,
    mohsHardness: 2.5,
    vickersHardness: 0.36,
    // Inverted-band semimetal at room temperature; reported as ~0 here so
    // the parallel-coords Band Gap axis still has a number for HgTe.
    bandGap: 0,
    electricalResistivity: 1e-4,
    dielectricConstant: 21,
    refractiveIndex: 3.79,
    magneticSusceptibility: -76,
  },

  // ─── Perovskites / titanates ───────────────────────────────────────────
  'Strontium Titanate': {
    formula: 'SrTiO₃',
    density: 5.11,
    meltingPoint: 2080,
    specificHeat: 0.544,
    thermalConductivity: 12,
    thermalExpansion: 9.4,
    debyeTemperature: 513,
    formationEnthalpy: -1675,
    youngsModulus: 270,
    bulkModulus: 179,
    shearModulus: 116,
    poissonRatio: 0.24,
    mohsHardness: 6.5,
    vickersHardness: 6,
    bandGap: 3.25,
    // Undoped, oxidized SrTiO₃ at RT — strongly reducible to n-type so
    // reported values vary by orders of magnitude with stoichiometry.
    electricalResistivity: 1e7,
    dielectricConstant: 300,
    refractiveIndex: 2.41,
    magneticSusceptibility: -28,
  },
  'Barium Titanate': {
    formula: 'BaTiO₃',
    density: 6.02,
    meltingPoint: 1625,
    specificHeat: 0.43,
    thermalConductivity: 6.2,
    thermalExpansion: 9,
    debyeTemperature: 480,
    formationEnthalpy: -1659,
    youngsModulus: 67,
    bulkModulus: 162,
    shearModulus: 54,
    poissonRatio: 0.35,
    mohsHardness: 5.0,
    vickersHardness: 6.5,
    bandGap: 3.2,
    electricalResistivity: 1e9,
    dielectricConstant: 1700,
    refractiveIndex: 2.4,
    magneticSusceptibility: -30,
    curieTemperature: 393,
  },

  // ─── Rutile / anatase / pyrite ─────────────────────────────────────────
  Rutile: {
    formula: 'TiO₂',
    density: 4.23,
    meltingPoint: 1843,
    boilingPoint: 2972,
    specificHeat: 0.711,
    thermalConductivity: 11.7,
    thermalExpansion: 9.0,
    formationEnthalpy: -944.0,
    youngsModulus: 282,
    bulkModulus: 211,
    shearModulus: 113,
    poissonRatio: 0.28,
    mohsHardness: 6.5,
    vickersHardness: 10,
    bandGap: 3.0,
    electricalResistivity: 1e10,
    dielectricConstant: 114,
    refractiveIndex: 2.61,
    // Pure TiO₂ is weakly paramagnetic at RT (Ti⁴⁺ d⁰, but defect-related
    // Curie tail from O-vacancy Ti³⁺ centers).
    magneticSusceptibility: 5.9,
  },
  Anatase: {
    formula: 'TiO₂',
    density: 3.895,
    meltingPoint: 1843,
    specificHeat: 0.683,
    thermalConductivity: 8.4,
    thermalExpansion: 9.5,
    debyeTemperature: 550,
    formationEnthalpy: -938.7,
    youngsModulus: 178,
    bulkModulus: 178,
    shearModulus: 70,
    poissonRatio: 0.27,
    mohsHardness: 5.75,
    vickersHardness: 5,
    bandGap: 3.2,
    electricalResistivity: 1e7,
    dielectricConstant: 48,
    refractiveIndex: 2.49,
    magneticSusceptibility: 5.0,
  },
  Pyrite: {
    formula: 'FeS₂',
    density: 5.01,
    meltingPoint: 1188,
    specificHeat: 0.517,
    thermalConductivity: 19,
    thermalExpansion: 8,
    debyeTemperature: 645,
    formationEnthalpy: -171.5,
    youngsModulus: 305,
    bulkModulus: 148,
    shearModulus: 126,
    poissonRatio: 0.21,
    mohsHardness: 6.25,
    vickersHardness: 9,
    bandGap: 0.95,
    electricalResistivity: 1e-3,
    dielectricConstant: 16,
    refractiveIndex: 1.4,
    // Fe²⁺ is low-spin in pyrite's distorted octahedral S₆ environment
    // (S=0), so it's strongly diamagnetic despite the iron content.
    magneticSusceptibility: -25,
  },

  // ─── Graphite / layered ────────────────────────────────────────────────
  Graphite: {
    formula: 'C',
    density: 2.267,
    // At 1 atm graphite sublimes rather than melting in the textbook sense;
    // 3825 °C is the sublimation temperature most tables cite. We populate
    // both fields with the same value so the parallel-coords Melting Point
    // axis isn't blank for graphite — physically the two are colocated at
    // standard pressure and users expect a number on that axis.
    meltingPoint: 3825,
    sublimationPoint: 3825,
    specificHeat: 0.71,
    thermalConductivity: 200,
    thermalExpansion: 7.0,
    debyeTemperature: 950,
    formationEnthalpy: 0,
    youngsModulus: 32,
    bulkModulus: 33,
    shearModulus: 4.5,
    poissonRatio: 0.25,
    mohsHardness: 1.5,
    // Highly anisotropic — basal-plane indentation is very soft due to
    // weak interlayer van der Waals bonding. The polycrystalline value
    // commonly reported.
    vickersHardness: 0.05,
    bandGap: 0,
    electricalResistivity: 1e-5,
    workFunction: 4.6,
    dielectricConstant: 12,
    refractiveIndex: 2.7,
    magneticSusceptibility: -22,
  },

  // ─── Pure metals ───────────────────────────────────────────────────────
  Copper: {
    formula: 'Cu',
    density: 8.96,
    meltingPoint: 1085,
    boilingPoint: 2562,
    specificHeat: 0.385,
    thermalConductivity: 401,
    thermalExpansion: 16.5,
    debyeTemperature: 343,
    formationEnthalpy: 0,
    youngsModulus: 130,
    bulkModulus: 140,
    shearModulus: 48,
    poissonRatio: 0.34,
    mohsHardness: 3.0,
    vickersHardness: 0.369,
    bandGap: 0,
    electricalResistivity: 1.68e-8,
    workFunction: 4.7,
    magneticSusceptibility: -5.46,
  },
  Iron: {
    formula: 'Fe',
    density: 7.874,
    meltingPoint: 1538,
    boilingPoint: 2862,
    specificHeat: 0.449,
    thermalConductivity: 80.2,
    thermalExpansion: 11.8,
    debyeTemperature: 470,
    formationEnthalpy: 0,
    youngsModulus: 211,
    bulkModulus: 170,
    shearModulus: 82,
    poissonRatio: 0.29,
    mohsHardness: 4.0,
    vickersHardness: 0.608,
    bandGap: 0,
    electricalResistivity: 9.71e-8,
    workFunction: 4.7,
    curieTemperature: 1043,
  },
  Aluminum: {
    formula: 'Al',
    density: 2.6989,
    meltingPoint: 660.32,
    boilingPoint: 2519,
    specificHeat: 0.897,
    thermalConductivity: 237,
    thermalExpansion: 23.1,
    debyeTemperature: 428,
    formationEnthalpy: 0,
    youngsModulus: 70,
    bulkModulus: 76,
    shearModulus: 26,
    poissonRatio: 0.35,
    mohsHardness: 2.75,
    vickersHardness: 0.167,
    bandGap: 0,
    electricalResistivity: 2.65e-8,
    workFunction: 4.08,
    magneticSusceptibility: 16.5,
  },
  Gold: {
    formula: 'Au',
    density: 19.32,
    meltingPoint: 1064.18,
    boilingPoint: 2856,
    specificHeat: 0.129,
    thermalConductivity: 318,
    thermalExpansion: 14.2,
    debyeTemperature: 165,
    formationEnthalpy: 0,
    youngsModulus: 78,
    bulkModulus: 180,
    shearModulus: 27,
    poissonRatio: 0.44,
    mohsHardness: 2.5,
    vickersHardness: 0.216,
    bandGap: 0,
    electricalResistivity: 2.214e-8,
    workFunction: 5.1,
    magneticSusceptibility: -28,
  },
  Silver: {
    formula: 'Ag',
    density: 10.49,
    meltingPoint: 961.78,
    boilingPoint: 2162,
    specificHeat: 0.235,
    thermalConductivity: 429,
    thermalExpansion: 18.9,
    debyeTemperature: 225,
    formationEnthalpy: 0,
    youngsModulus: 83,
    bulkModulus: 100,
    shearModulus: 30,
    poissonRatio: 0.37,
    mohsHardness: 2.5,
    vickersHardness: 0.251,
    bandGap: 0,
    electricalResistivity: 1.59e-8,
    workFunction: 4.26,
    magneticSusceptibility: -19.5,
  },
  Platinum: {
    formula: 'Pt',
    density: 21.45,
    meltingPoint: 1768.3,
    boilingPoint: 3825,
    specificHeat: 0.133,
    thermalConductivity: 71.6,
    thermalExpansion: 8.8,
    debyeTemperature: 240,
    youngsModulus: 168,
    bulkModulus: 230,
    shearModulus: 61,
    poissonRatio: 0.38,
    mohsHardness: 4.5,
    vickersHardness: 0.549,
    bandGap: 0,
    electricalResistivity: 1.06e-7,
    workFunction: 5.65,
    magneticSusceptibility: 201.9,
  },
  Tungsten: {
    formula: 'W',
    density: 19.25,
    meltingPoint: 3422,
    boilingPoint: 5555,
    specificHeat: 0.132,
    thermalConductivity: 173,
    thermalExpansion: 4.5,
    debyeTemperature: 400,
    youngsModulus: 411,
    bulkModulus: 310,
    shearModulus: 161,
    poissonRatio: 0.28,
    mohsHardness: 7.5,
    vickersHardness: 3.43,
    bandGap: 0,
    electricalResistivity: 5.28e-8,
    workFunction: 4.55,
    magneticSusceptibility: 59,
  },
  Nickel: {
    formula: 'Ni',
    density: 8.908,
    meltingPoint: 1455,
    boilingPoint: 2913,
    specificHeat: 0.444,
    thermalConductivity: 90.9,
    thermalExpansion: 13.4,
    debyeTemperature: 450,
    youngsModulus: 200,
    bulkModulus: 180,
    shearModulus: 76,
    poissonRatio: 0.31,
    mohsHardness: 4.0,
    vickersHardness: 0.638,
    bandGap: 0,
    electricalResistivity: 6.99e-8,
    workFunction: 5.15,
    curieTemperature: 627,
  },
  Cobalt: {
    formula: 'Co',
    density: 8.86,
    meltingPoint: 1495,
    boilingPoint: 2927,
    specificHeat: 0.421,
    thermalConductivity: 100,
    thermalExpansion: 13.0,
    debyeTemperature: 445,
    youngsModulus: 209,
    bulkModulus: 180,
    shearModulus: 75,
    poissonRatio: 0.31,
    mohsHardness: 5.0,
    vickersHardness: 1.043,
    bandGap: 0,
    electricalResistivity: 6.24e-8,
    workFunction: 5.0,
    curieTemperature: 1388,
  },
  Vanadium: {
    formula: 'V',
    density: 6.0,
    meltingPoint: 1910,
    boilingPoint: 3407,
    specificHeat: 0.489,
    thermalConductivity: 30.7,
    thermalExpansion: 8.4,
    debyeTemperature: 380,
    formationEnthalpy: 0,
    youngsModulus: 128,
    bulkModulus: 160,
    shearModulus: 47,
    poissonRatio: 0.37,
    mohsHardness: 7.0,
    vickersHardness: 0.628,
    bandGap: 0,
    electricalResistivity: 1.97e-7,
    workFunction: 4.3,
    magneticSusceptibility: 285,
  },
  Titanium: {
    formula: 'Ti',
    density: 4.506,
    meltingPoint: 1668,
    boilingPoint: 3287,
    specificHeat: 0.523,
    thermalConductivity: 21.9,
    thermalExpansion: 8.6,
    debyeTemperature: 420,
    formationEnthalpy: 0,
    youngsModulus: 116,
    bulkModulus: 110,
    shearModulus: 44,
    poissonRatio: 0.32,
    mohsHardness: 6.0,
    vickersHardness: 0.97,
    bandGap: 0,
    electricalResistivity: 4.2e-7,
    workFunction: 4.33,
    magneticSusceptibility: 153,
  },
  Magnesium: {
    formula: 'Mg',
    density: 1.738,
    meltingPoint: 650,
    boilingPoint: 1090,
    specificHeat: 1.023,
    thermalConductivity: 156,
    thermalExpansion: 24.8,
    debyeTemperature: 318,
    formationEnthalpy: 0,
    youngsModulus: 45,
    bulkModulus: 45,
    shearModulus: 17,
    poissonRatio: 0.29,
    mohsHardness: 2.5,
    vickersHardness: 0.26,
    bandGap: 0,
    electricalResistivity: 4.39e-8,
    workFunction: 3.66,
    magneticSusceptibility: 13.1,
  },
  Lithium: {
    formula: 'Li',
    density: 0.534,
    meltingPoint: 180.5,
    boilingPoint: 1342,
    specificHeat: 3.582,
    thermalConductivity: 84.8,
    thermalExpansion: 46,
    debyeTemperature: 344,
    formationEnthalpy: 0,
    youngsModulus: 4.9,
    bulkModulus: 11,
    shearModulus: 4.2,
    poissonRatio: 0.36,
    mohsHardness: 0.6,
    bandGap: 0,
    electricalResistivity: 9.28e-8,
    workFunction: 2.9,
    magneticSusceptibility: 14.2,
  },
  Sodium: {
    formula: 'Na',
    density: 0.971,
    meltingPoint: 97.79,
    boilingPoint: 882.85,
    specificHeat: 1.228,
    thermalConductivity: 142,
    thermalExpansion: 71,
    debyeTemperature: 158,
    formationEnthalpy: 0,
    youngsModulus: 10,
    bulkModulus: 6.3,
    shearModulus: 3.3,
    poissonRatio: 0.34,
    mohsHardness: 0.5,
    bandGap: 0,
    electricalResistivity: 4.77e-8,
    workFunction: 2.36,
    magneticSusceptibility: 16,
  },
  Zinc: {
    formula: 'Zn',
    density: 7.14,
    meltingPoint: 419.53,
    boilingPoint: 907,
    specificHeat: 0.388,
    thermalConductivity: 116,
    thermalExpansion: 30.2,
    debyeTemperature: 327,
    formationEnthalpy: 0,
    youngsModulus: 108,
    bulkModulus: 70,
    shearModulus: 43,
    poissonRatio: 0.25,
    mohsHardness: 2.5,
    vickersHardness: 0.412,
    bandGap: 0,
    electricalResistivity: 5.9e-8,
    workFunction: 4.33,
    magneticSusceptibility: -11.4,
  },
  Chromium: {
    formula: 'Cr',
    density: 7.19,
    meltingPoint: 1907,
    boilingPoint: 2671,
    specificHeat: 0.448,
    thermalConductivity: 93.9,
    thermalExpansion: 4.9,
    debyeTemperature: 630,
    formationEnthalpy: 0,
    youngsModulus: 279,
    bulkModulus: 160,
    shearModulus: 115,
    poissonRatio: 0.21,
    mohsHardness: 8.5,
    vickersHardness: 1.06,
    bandGap: 0,
    electricalResistivity: 1.25e-7,
    workFunction: 4.5,
    magneticSusceptibility: 280,
    // Antiferromagnetic at room temperature; Néel temperature ≈ 311 K.
    curieTemperature: 311,
  },
  Tantalum: {
    formula: 'Ta',
    density: 16.69,
    meltingPoint: 3017,
    boilingPoint: 5458,
    specificHeat: 0.14,
    thermalConductivity: 57.5,
    thermalExpansion: 6.3,
    debyeTemperature: 240,
    formationEnthalpy: 0,
    youngsModulus: 186,
    bulkModulus: 200,
    shearModulus: 69,
    poissonRatio: 0.34,
    mohsHardness: 6.5,
    vickersHardness: 0.873,
    bandGap: 0,
    electricalResistivity: 1.31e-7,
    workFunction: 4.25,
    magneticSusceptibility: 154,
  },
  Molybdenum: {
    formula: 'Mo',
    density: 10.28,
    meltingPoint: 2623,
    boilingPoint: 4639,
    specificHeat: 0.251,
    thermalConductivity: 138,
    thermalExpansion: 4.8,
    debyeTemperature: 450,
    youngsModulus: 329,
    bulkModulus: 230,
    shearModulus: 126,
    poissonRatio: 0.31,
    mohsHardness: 5.5,
    vickersHardness: 1.5,
    bandGap: 0,
    electricalResistivity: 5.34e-8,
    workFunction: 4.6,
    magneticSusceptibility: 89,
  },
  Palladium: {
    formula: 'Pd',
    density: 12.02,
    meltingPoint: 1554.9,
    boilingPoint: 2963,
    specificHeat: 0.246,
    thermalConductivity: 71.8,
    thermalExpansion: 11.8,
    debyeTemperature: 274,
    youngsModulus: 121,
    bulkModulus: 180,
    shearModulus: 44,
    poissonRatio: 0.39,
    mohsHardness: 4.75,
    vickersHardness: 0.461,
    bandGap: 0,
    electricalResistivity: 1.05e-7,
    workFunction: 5.22,
    magneticSusceptibility: 567.4,
  },
  Zirconium: {
    formula: 'Zr',
    density: 6.52,
    meltingPoint: 1855,
    boilingPoint: 4377,
    specificHeat: 0.278,
    thermalConductivity: 22.7,
    thermalExpansion: 5.7,
    debyeTemperature: 291,
    formationEnthalpy: 0,
    youngsModulus: 88,
    bulkModulus: 91,
    shearModulus: 33,
    poissonRatio: 0.34,
    mohsHardness: 5.0,
    vickersHardness: 0.903,
    bandGap: 0,
    electricalResistivity: 4.21e-7,
    workFunction: 4.05,
    magneticSusceptibility: 122,
  },
  Beryllium: {
    formula: 'Be',
    density: 1.85,
    meltingPoint: 1287,
    boilingPoint: 2469,
    specificHeat: 1.825,
    thermalConductivity: 200,
    thermalExpansion: 11.3,
    debyeTemperature: 1440,
    formationEnthalpy: 0,
    youngsModulus: 287,
    bulkModulus: 130,
    shearModulus: 132,
    poissonRatio: 0.032,
    mohsHardness: 5.5,
    vickersHardness: 1.67,
    bandGap: 0,
    electricalResistivity: 3.56e-8,
    workFunction: 4.98,
    magneticSusceptibility: -9.0,
  },

  // ─── Intermetallic alloys ──────────────────────────────────────────────
  'Copper-Gold (Cu3Au)': {
    formula: 'Cu₃Au',
    density: 12.4,
    meltingPoint: 889,
    specificHeat: 0.231,
    thermalConductivity: 180,
    thermalExpansion: 16,
    formationEnthalpy: -7.4,
    youngsModulus: 120,
    bulkModulus: 144,
    shearModulus: 45,
    poissonRatio: 0.34,
    mohsHardness: 3.5,
    vickersHardness: 1.0,
    bandGap: 0,
    electricalResistivity: 1.5e-7,
  },
  'Gold-Copper (Au3Cu)': {
    formula: 'Au₃Cu',
    density: 17.0,
    meltingPoint: 940,
    specificHeat: 0.155,
    thermalConductivity: 200,
    thermalExpansion: 14.5,
    youngsModulus: 110,
    bulkModulus: 175,
    mohsHardness: 3.0,
    bandGap: 0,
    electricalResistivity: 1.0e-7,
  },
  'Copper-Gold (CuAu)': {
    formula: 'CuAu',
    density: 14.7,
    meltingPoint: 911,
    specificHeat: 0.184,
    thermalConductivity: 130,
    thermalExpansion: 14,
    youngsModulus: 105,
    bulkModulus: 155,
    mohsHardness: 3.0,
    bandGap: 0,
    electricalResistivity: 1.3e-7,
  },
  'Beta-Brass': {
    formula: 'CuZn',
    density: 8.32,
    meltingPoint: 902,
    specificHeat: 0.375,
    thermalConductivity: 121,
    thermalExpansion: 19,
    debyeTemperature: 320,
    formationEnthalpy: -7.5,
    youngsModulus: 102,
    bulkModulus: 118,
    shearModulus: 40,
    poissonRatio: 0.35,
    mohsHardness: 3.5,
    bandGap: 0,
    electricalResistivity: 6.5e-8,
  },
  'Nickel Aluminide': {
    formula: 'NiAl',
    density: 5.85,
    meltingPoint: 1638,
    specificHeat: 0.537,
    thermalConductivity: 76,
    thermalExpansion: 13.2,
    debyeTemperature: 450,
    formationEnthalpy: -59,
    youngsModulus: 188,
    bulkModulus: 156,
    shearModulus: 72,
    poissonRatio: 0.31,
    mohsHardness: 6.5,
    vickersHardness: 2.6,
    bandGap: 0,
    electricalResistivity: 8e-8,
  },
  'Nickel Aluminide (Ni3Al)': {
    formula: 'Ni₃Al',
    density: 7.5,
    meltingPoint: 1395,
    specificHeat: 0.486,
    thermalConductivity: 28,
    thermalExpansion: 12.5,
    debyeTemperature: 430,
    formationEnthalpy: -38,
    youngsModulus: 180,
    bulkModulus: 174,
    shearModulus: 78,
    poissonRatio: 0.31,
    mohsHardness: 6.0,
    vickersHardness: 3.0,
    bandGap: 0,
    electricalResistivity: 3.3e-7,
  },
  'Nickel Aluminide (NiAl3)': {
    formula: 'NiAl₃',
    density: 3.95,
    meltingPoint: 854, // peritectic decomposition, not congruent melting
    specificHeat: 0.65,
    thermalExpansion: 16,
    formationEnthalpy: -38, // per mol-atom, as for the other Ni-Al phases here
    youngsModulus: 152,
    vickersHardness: 5.0,
    mohsHardness: 5.0,
    bandGap: 0,
  },

  // ─── Molecular crystals (van der Waals / hydrogen-bonded) ──────────────
  'Dry Ice': {
    formula: 'CO₂',
    density: 1.562,
    sublimationPoint: -78.5,
    specificHeat: 0.844,
    thermalConductivity: 0.7,
    debyeTemperature: 145,
    formationEnthalpy: -393.5,
    youngsModulus: 6,
    bulkModulus: 4,
    poissonRatio: 0.31,
    mohsHardness: 1.5,
    bandGap: 8.7,
    dielectricConstant: 1.6,
    refractiveIndex: 1.4,
    // Molar χ values for the molecular crystals below are the molecule's
    // measured χ_M (CRC / Landolt-Börnstein); since χ is molar and the
    // electronic structure barely changes from gas to crystal for closed-
    // shell van der Waals solids, the same value applies to the crystal.
    magneticSusceptibility: -21,
  },
  // Static dielectric constants on the noble-gas / N₂ solids are from
  // low-temperature capacitance measurements (Werner / Sinnock / Smyth)
  // — all close to (n²) since these are non-polar van der Waals crystals
  // with no orientational dipole contribution.
  Nitrogen: {
    formula: 'N₂',
    density: 1.026,
    meltingPoint: -210.0,
    boilingPoint: -195.8,
    specificHeat: 1.84,
    thermalConductivity: 0.27,
    debyeTemperature: 68,
    formationEnthalpy: 0,
    youngsModulus: 1.5,
    bulkModulus: 1.1,
    poissonRatio: 0.28,
    mohsHardness: 0.5,
    dielectricConstant: 1.45,
    refractiveIndex: 1.21,
    magneticSusceptibility: -12,
  },
  Argon: {
    formula: 'Ar',
    density: 1.66,
    meltingPoint: -189.34,
    boilingPoint: -185.85,
    specificHeat: 0.52,
    thermalConductivity: 0.31,
    debyeTemperature: 85,
    formationEnthalpy: 0,
    bulkModulus: 2.7,
    poissonRatio: 0.25,
    dielectricConstant: 1.66,
    refractiveIndex: 1.29,
    magneticSusceptibility: -19.6,
  },
  Krypton: {
    formula: 'Kr',
    density: 2.413,
    meltingPoint: -157.36,
    boilingPoint: -153.22,
    specificHeat: 0.248,
    thermalConductivity: 0.495,
    debyeTemperature: 72,
    formationEnthalpy: 0,
    bulkModulus: 3.0,
    poissonRatio: 0.27,
    dielectricConstant: 1.88,
    refractiveIndex: 1.305,
    magneticSusceptibility: -28,
  },
  Xenon: {
    formula: 'Xe',
    density: 3.52,
    meltingPoint: -111.75,
    boilingPoint: -108.09,
    specificHeat: 0.158,
    thermalConductivity: 0.39,
    debyeTemperature: 64,
    formationEnthalpy: 0,
    bulkModulus: 3.5,
    poissonRatio: 0.3,
    dielectricConstant: 2.12,
    refractiveIndex: 1.49,
    magneticSusceptibility: -43.9,
  },
  Neon: {
    formula: 'Ne',
    density: 1.207,
    meltingPoint: -248.59,
    boilingPoint: -246.05,
    specificHeat: 1.03,
    thermalConductivity: 0.13,
    debyeTemperature: 75,
    formationEnthalpy: 0,
    bulkModulus: 1.1,
    poissonRatio: 0.2,
    dielectricConstant: 1.24,
    refractiveIndex: 1.092,
    magneticSusceptibility: -7.0,
  },
  // Keyed 'Cubic Ice' to match the preset name (iceStructure.ts builds
  // ice Ic via buildCubicIce()). Ice Ic and Ih have nearly identical
  // bulk properties — the difference is only the stacking sequence — so
  // CRC/Springer hexagonal-ice values are reused here.
  'Cubic Ice': {
    formula: 'H₂O',
    density: 0.917,
    meltingPoint: 0,
    boilingPoint: 100,
    specificHeat: 2.09,
    thermalConductivity: 2.22,
    thermalExpansion: 51,
    formationEnthalpy: -291.8,
    youngsModulus: 9.1,
    bulkModulus: 8.8,
    shearModulus: 3.6,
    poissonRatio: 0.33,
    mohsHardness: 1.5,
    bandGap: 7.9,
    dielectricConstant: 97.5,
    refractiveIndex: 1.31,
    magneticSusceptibility: -13,
  },
  Methane: {
    formula: 'CH₄',
    density: 0.493,
    meltingPoint: -182.5,
    boilingPoint: -161.5,
    specificHeat: 2.22,
    thermalConductivity: 0.34,
    debyeTemperature: 142,
    formationEnthalpy: -74.6,
    bulkModulus: 1.5,
    mohsHardness: 0.5,
    refractiveIndex: 1.3,
    magneticSusceptibility: -17.4,
  },
  Hydrogen: {
    formula: 'H₂',
    density: 0.0855,
    meltingPoint: -259.16,
    boilingPoint: -252.87,
    specificHeat: 14.3,
    thermalConductivity: 0.103,
    debyeTemperature: 119,
    formationEnthalpy: 0,
    bulkModulus: 0.17,
    refractiveIndex: 1.11,
    magneticSusceptibility: -4,
  },
  'Carbon Monoxide': {
    formula: 'CO',
    density: 0.789,
    meltingPoint: -205.02,
    boilingPoint: -191.5,
    specificHeat: 1.04,
    thermalConductivity: 0.18,
    debyeTemperature: 88,
    formationEnthalpy: -110.5,
    refractiveIndex: 1.34,
    magneticSusceptibility: -10,
  },
  Ammonia: {
    formula: 'NH₃',
    density: 0.817,
    meltingPoint: -77.73,
    boilingPoint: -33.34,
    specificHeat: 4.7,
    thermalConductivity: 0.6,
    debyeTemperature: 285,
    formationEnthalpy: -45.9,
    bulkModulus: 6.5,
    mohsHardness: 1.0,
    dielectricConstant: 22,
    refractiveIndex: 1.325,
    magneticSusceptibility: -18,
  },
  'Sulfur Hexafluoride': {
    formula: 'SF₆',
    density: 1.88,
    sublimationPoint: -64,
    specificHeat: 0.665,
    thermalConductivity: 0.013,
    formationEnthalpy: -1220.5,
    refractiveIndex: 1.0008,
    dielectricConstant: 1.002,
    magneticSusceptibility: -45,
  },
  Acetylene: {
    formula: 'C₂H₂',
    density: 0.729,
    meltingPoint: -80.8,
    sublimationPoint: -84,
    specificHeat: 1.69,
    thermalConductivity: 0.02,
    formationEnthalpy: 226.7,
    refractiveIndex: 1.0006,
    magneticSusceptibility: -21,
  },
  'Carbon Tetrafluoride': {
    formula: 'CF₄',
    density: 1.96,
    meltingPoint: -183.6,
    boilingPoint: -128,
    specificHeat: 0.71,
    formationEnthalpy: -933.6,
    refractiveIndex: 1.151,
    magneticSusceptibility: -28,
  },
  'Carbon Tetrachloride': {
    formula: 'CCl₄',
    density: 1.594,
    meltingPoint: -22.92,
    boilingPoint: 76.72,
    specificHeat: 0.857,
    thermalConductivity: 0.1,
    formationEnthalpy: -95.7,
    dielectricConstant: 2.24,
    refractiveIndex: 1.46,
    magneticSusceptibility: -66,
  },
  'Carbon Tetrabromide': {
    formula: 'CBr₄',
    density: 3.42,
    meltingPoint: 90.1,
    boilingPoint: 189.5,
    specificHeat: 0.45,
    formationEnthalpy: 18.8,
    refractiveIndex: 1.59,
    magneticSusceptibility: -94,
  },
  'Nitrous Oxide': {
    formula: 'N₂O',
    density: 1.226,
    meltingPoint: -90.86,
    boilingPoint: -88.48,
    specificHeat: 0.88,
    thermalConductivity: 0.017,
    formationEnthalpy: 82.05,
    refractiveIndex: 1.241,
    magneticSusceptibility: -19,
  },
  'Hydrogen Cyanide': {
    formula: 'HCN',
    density: 0.687,
    meltingPoint: -13.29,
    boilingPoint: 25.6,
    specificHeat: 2.61,
    thermalConductivity: 0.43,
    formationEnthalpy: 135.1,
    dielectricConstant: 158.7,
    refractiveIndex: 1.262,
    magneticSusceptibility: -16,
  },
  'White Phosphorus': {
    formula: 'P',
    density: 1.823,
    meltingPoint: 44.15,
    boilingPoint: 280.5,
    specificHeat: 0.769,
    thermalConductivity: 0.236,
    formationEnthalpy: 0,
    bulkModulus: 5.6,
    mohsHardness: 0.5,
    bandGap: 2.1,
    dielectricConstant: 4.1,
    refractiveIndex: 1.8244,
    magneticSusceptibility: -20,
  },
  Cyanogen: {
    formula: 'C₂N₂',
    density: 0.95,
    meltingPoint: -27.83,
    boilingPoint: -21.17,
    specificHeat: 1.13,
    formationEnthalpy: 308.4,
    refractiveIndex: 1.327,
    magneticSusceptibility: -22,
  },
  Silane: {
    formula: 'SiH₄',
    density: 0.711,
    meltingPoint: -185,
    boilingPoint: -111.9,
    specificHeat: 1.39,
    thermalConductivity: 0.022,
    formationEnthalpy: 34.3,
    refractiveIndex: 1.0009,
    magneticSusceptibility: -25,
  },
  Phosphine: {
    formula: 'PH₃',
    density: 0.746,
    meltingPoint: -133.5,
    boilingPoint: -87.7,
    specificHeat: 1.02,
    thermalConductivity: 0.019,
    formationEnthalpy: 5.4,
    refractiveIndex: 1.317,
    magneticSusceptibility: -27,
  },
  Germane: {
    formula: 'GeH₄',
    density: 1.52,
    meltingPoint: -165,
    boilingPoint: -88.5,
    specificHeat: 0.55,
    formationEnthalpy: 90.8,
    magneticSusceptibility: -39,
  },
  'Carbon Disulfide': {
    formula: 'CS₂',
    density: 1.539,
    meltingPoint: -111.5,
    boilingPoint: 46.24,
    specificHeat: 1.0,
    thermalConductivity: 0.149,
    formationEnthalpy: 89.7,
    dielectricConstant: 2.63,
    refractiveIndex: 1.628,
    magneticSusceptibility: -42,
  },
  Arsine: {
    formula: 'AsH₃',
    density: 1.689,
    meltingPoint: -111.2,
    boilingPoint: -62.5,
    specificHeat: 0.42,
    formationEnthalpy: 66.4,
    refractiveIndex: 1.394,
    magneticSusceptibility: -39,
  },
};
